import { Icon } from 'components/common';
import PrimeBadge from 'components/common/PrimeBadge';
import { BJProfile } from 'types/bjProfile';
import { getPrimeState } from 'utils/primeUtils';
import { getAge, getFlagUrl, getUserLocalTime } from 'utils/userUtils';

type ProfileInfoProps = {
  userInfo: UserInfo;
  isPrime?: boolean;
  bjProfile?: BJProfile | null;
};

const ProfileInfo = ({ userInfo, isPrime = false, bjProfile = null }: ProfileInfoProps) => {
  const isTeamBermuda = userInfo?.fbUid === PageLogin.fbIdBermuda;

  if (!userInfo) return <></>;
  return (
    <div data-component="ProfileInfo" className="my-4 flex w-full flex-col gap-2">
      <NicknameAndIcons userInfo={userInfo} isTeamBermuda={isTeamBermuda} />
      <UidCopyButton uid={userInfo.fbUid} isPrime={isPrime} />
      <TeamBermudaButton isTeamBermuda={isTeamBermuda} />
      {!isTeamBermuda && (
        <div className="flex flex-wrap items-center gap-2 justify-start">
          <LocationInfo userInfo={userInfo} />
          {isPrime ? <PrimeChips bjProfile={bjProfile} /> : <TimeInfo userInfo={userInfo} />}
        </div>
      )}
      <Divider userInfo={userInfo} />
      <PreferredPartnerInfo userInfo={userInfo} />
      {showsSubscriptionInfo(userInfo) && <SubscriptionInfo />}
    </div>
  );
};

const NicknameAndIcons = ({ userInfo, isTeamBermuda }: { userInfo: UserInfo; isTeamBermuda: boolean }) => {
  const { nick, signup_preference, gender } = userInfo;
  const age = signup_preference?.birthday ? `, ${getAge(new Date(signup_preference.birthday))}` : '';

  return (
    <div className="flex items-center">
      {!isTeamBermuda && gender && <GenderIcon gender={gender} />}
      <div className="w-1" />
      <div className="typography-headline-lg font-bold">
        {nick}
        {age}
      </div>
      <Police2Badge uid={userInfo.fbUid} />
    </div>
  );
};

const GenderIcon = ({ gender }: { gender: 'male' | 'female' }) => {
  return (
    <div className="flex h-6 w-6 items-center justify-center">
      <img className="w-6" alt="" src={`/imgweb/icon/ic_${gender}.svg`} />
    </div>
  );
};

const UidCopyButton = ({ uid, isPrime }: { uid: string; isPrime: boolean }) => {
  const isMe = uid === myinfo.fbUid;
  const isPolice = MyDataManager.getPoliceVal() > 0;
  const visible = isPrime ? PrimeCommon.isPrimeAdmin() : isMe || isPolice;

  const onClick = () => {
    navigator.clipboard.writeText(uid);
    CommonUI.showToast(document.getElementById('hMainContainer'), { text: brsc.copied_clipboard, position: 'top' });
  };

  if (visible) {
    return (
      <div className="bg-base-bg-subtle flex w-fit items-center gap-1 rounded-sm px-2 py-1 typography-body-sm" onClick={onClick}>
        <div className="font-bold">UID</div>
        <div>{uid}</div>
        <Icon name={bassets.bermuda.icon_set.copy.svg} size={14} />
      </div>
    );
  }

  return <></>;
};

const Police2Badge = ({ uid }: { uid: string }) => {
  if (uid !== myinfo.fbUid || MyDataManager.getPoliceVal() !== 2) return null;

  return (
    <div className="ms-auto shrink-0 typography-body-sm font-bold underline text-base-txt-primary">
      POLICE 2
    </div>
  );
};

const TeamBermudaButton = ({ isTeamBermuda }: { isTeamBermuda: boolean }) => {
  if (!isTeamBermuda) return null;

  return (
    <div className="bg-base-bg-subtle flex w-fit items-center gap-1 rounded-sm px-2 py-1 typography-body-sm">
      <img className="h-5 w-5" alt="" src="/imgweb/main/logo_flat.svg" />
      Swipe to meet friends
    </div>
  );
};

const LocationInfo = ({ userInfo }: { userInfo: UserInfo }) => {
  const { country, specialLocale, locale } = userInfo;
  let location = getCountryName(country);
  if (specialLocale) {
    location = `${MembershipCommon.getLocation(specialLocale)}, ${getCountryName(MembershipCommon.getCountryCode(specialLocale))}`;
  }
  const langName = BResource.getLocaleDispname(locale);

  return (
    <div className="bg-base-bg-subtle flex w-fit items-center gap-1 rounded-sm px-2 py-1 typography-body-sm min-w-0">
      <img className="h-6 w-6" alt="" src={getFlagUrl(country, specialLocale)} />
      <div className="break-words">{location}</div>
      {langName && <div>{'\u2022'}</div>}
      {langName && <div>{langName}</div>}
    </div>
  );
};

const TimeInfo = ({ userInfo }: { userInfo: UserInfo }) => {
  const { tzoffset } = userInfo;
  if (!tzoffset) return <></>;
  const { dayOrNight, timeString } = getUserLocalTime(tzoffset);
  return (
    <div className="bg-base-bg-subtle flex w-fit items-center gap-1 rounded-sm px-2 py-1 typography-body-sm">
      <img className="w-6" alt="" src={`/imgweb/chat/ic_${dayOrNight}.svg`} />
      <div className="">{timeString}</div>
    </div>
  );
};

const PrimeChips = ({ bjProfile }: { bjProfile: BJProfile | null }) => {
  if (!bjProfile) return <></>;
  const state = getPrimeState(bjProfile);
  return (
    <>
      <PrimeBadge cost={bjProfile.cost} perSecond={10} />
      {state && (
        <div className="bg-base-bg-subtle flex w-fit items-center gap-1 rounded-sm px-2 py-1 typography-body-sm">
          <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: state.color }} />
          <div>{state.label}</div>
        </div>
      )}
    </>
  );
};

const showsSubscriptionInfo = (userInfo: UserInfo) => userInfo.fbUid === myinfo.fbUid && Device.name !== 'web';

const Divider = ({ userInfo }: { userInfo: UserInfo }) => {
  if (!showsSubscriptionInfo(userInfo) && !userInfo.signup_preference?.preferRelationship) return null;

  return <div className="my-6 h-[1px] w-full" style={{ backgroundColor: 'var(--bcs-semantic-base-border-default)' }} />;
};

const PreferredPartnerInfo = ({ userInfo }: { userInfo: UserInfo }) => {
  if (!userInfo.signup_preference?.preferRelationship) return null;

  return (
    <div className="mb-4 flex flex-1 flex-col gap-2">
      <div className="text-base-txt-secondary">{brsc.preferred_partner}</div>
      <div
        className="relationship-item bg-base-bg-subtle flex justify-center gap-2 rounded-lg p-4 font-bold"
        data-value={userInfo.signup_preference?.preferRelationship}
        dangerouslySetInnerHTML={{ __html: brsc[`relationship_label_${userInfo.signup_preference?.preferRelationship}`] }}
      />
    </div>
  );
};

const subscriptionToProductId = (subscription: string) => {
  switch (subscription) {
    case 'lite':
      return 'csubs_6000';
    case 'membership':
      return 'csubs_12000';
    case 'premium':
      return Device.name === 'iOS' ? 'csubs_30001' : 'csubs_30000';
    default:
      return '';
  }
};

export const SubscriptionInfo = () => {
  const SubscriptionItem = ({ subscription, active }: { subscription: string; active: boolean }) => {
    return (
      <div
        onClick={() => PageShop.handleProductAction(subscriptionToProductId(subscription))}
        className="bg-base-bg-subtle flex flex-1 flex-col items-center justify-center gap-2 rounded-sm p-2 shadow-01"
        style={{ opacity: active ? 1 : 0.4 }}
      >
        <img className="h-10 w-10" alt="" src={`/imgweb/membership/ic_${subscription}.svg`} />
        <div className="text-base-txt-secondary">{subscription}</div>
      </div>
    );
  };

  return (
    <div className="flex w-full flex-1 flex-col gap-2">
      <div className="text-base-txt-secondary">{brsc.subscribed_plan}</div>
      <div className="flex w-full gap-2">
        <SubscriptionItem subscription="lite" active={LiteDataManager.getState() === 1} />
        <SubscriptionItem subscription="membership" active={MembershipDataManager.getState() === 1} />
        <SubscriptionItem subscription="premium" active={PremiumDataManager.getState() === 1} />
      </div>
    </div>
  );
};

export default ProfileInfo;
